import { useCallback, useContext, useEffect, useMemo, useState } from "react";

import { debounce } from "@mui/material";

import { RootContext } from "../context/RootContext";
import { LoansContext } from "../context/LoansContext";

import {
  getAutocompleteAddress,
  getBorrowerDetailsBasedOnEmail,
  getGuarantorBasedOnBorrowerId,
  getZipCodeList,
} from "../services/network";

import { ZipCodeSuggestion } from "../types/fieldTypes";
import { ObjectType } from "../types/tableTypes";
import { getAutoCalculatedResults } from "../utils/Common";
import {
  DEFAULT_AUDIT_COMMENT,
  getAuditFieldPath,
} from "../constants/auditFields";

interface Field {
  id: string;
  getFieldErrors?: (value: any) => string;
  [key: string]: any;
}

interface Unit {
  [key: string]: any;
}

interface Property {
  propertyLocation?: any;
  propertyInformation?: any;
  propertyEconomics?: any;
  unitInformation?: Unit[];
  [key: string]: any;
}

interface DetailedLoanSizingData {
  propertyDetails?: Property[];
  [key: string]: any;
}

// Constants
const PROPERTY_LEVEL_SECTIONS = [
  "propertyLocation",
  "propertyInformation",
  "propertyEconomics",
  "unitInformation",
] as const;

const PROPERTY_LOCATION_FIELDS = ["postalCode", "address"] as const;

// Credit-Committee-only manual pricing overrides (loanEconomics.interestRate /
// .finalToorakPrice — see LoanEconomics.ts's update_pricing-gated fields) get
// sent to the rules engine as loanPricing.actualInterestRate /
// .finalToorakPrice (src/utils/v2/common.ts). Once set, they're only valid
// for the inputs they were set against — editing anything else on the loan
// re-triggers a full rules-engine evaluation anyway, so a stale manual
// override would silently keep overriding a recalculated result. Editing any
// field OTHER than these two clears them instead, and records the clear as a
// real audit edit so the prior value stays visible in that field's audit
// trail.
const PRICING_OVERRIDE_FIELDS = ["interestRate", "finalToorakPrice"] as const;
const PRICING_OVERRIDE_SECTION = "loanEconomics";

// The display string for just the field the user directly interacted with
// (address or postalCode itself) — not the raw Smarty Streets suggestion
// object the Autocomplete hands back, which would otherwise render as
// "[object Object]" in the audit trail (AuditTag.tsx). Used both for that
// field's own free-typed edits (see addressOrPostalOnInputChange, which never
// derives city/state/postalCode on its own) and as a building block of
// getAddressDerivedFields below.
const getAddressFieldDisplayValue = (value: any, isPostalCode: boolean): any => {
  if (isPostalCode) return value?.zipCode || value;
  const { street_line, city, state } = value || {};
  return street_line && city && state ? `${street_line} ${city} ${state}` : value;
};

// Every field that selecting an address/postalCode suggestion actually
// updates in detailedLoanSizingData — picking an address suggestion also
// fills in city/state/postalCode, and picking a zip fills in city/state.
// This is the single source of truth for that: updatePostalOrAddress uses it
// for the real state write, and handleChange uses it to record an audit
// entry for every one of these fields, not just the one the user directly
// interacted with — otherwise city/state (and postalCode, when address
// changes) would silently change with no audit trail at all.
const getAddressDerivedFields = (
  value: any,
  id: string,
  isPostalCode: boolean,
): Record<string, any> => {
  if (isPostalCode) {
    return {
      [id]: getAddressFieldDisplayValue(value, true),
      city: value?.city || "",
      state: value?.state_abbreviation || "",
    };
  }
  const { city, state, zipcode } = value || {};
  return {
    [id]: getAddressFieldDisplayValue(value, false),
    city: city || "",
    state: state || "",
    postalCode: zipcode || "",
  };
};

const SPECIAL_FIELD_UPDATES = {
  interestOnlyFlag: (value: string, id?: number) => ({
    ioPeriod: value === "Yes" ? "120" : null,
  }),
  prepaymentPenaltyTypeMonths: (value: any, id?: number) => ({
    prepayPenaltyType: value === 0 ? 0 : null,
  }),
  borrowerType: (id?: number) => ({
    firstTimeHomeBuyer: null,
  }),
  numberOfProperties: (value: any, id?: number) => ({
    crossCollaterlization: value > 1 ? "Y" : "N",
  }),
  loanPurpose: (value: string, id?: number) => ({
    cashOutFlag: value === "Purchase" ? "No Cash Out" : "No Cash",
  }),
};

const normalizeEmail = (email: any) =>
  String(email ?? "")
    .trim()
    .toLowerCase();

// A guarantor is an existing one only while it is linked to a borrowerId AND
// its email still matches the email it was linked with
// (existingGuarantorEmail). A changed email — or no link at all — makes it a
// new guarantor. tc-bff-service's /submit-to-los reads this flag
// (updatedExistingBorrower) from the borrowers payload; false by default.
// It is also true while the credit report info of a linked guarantor differs
// from existingGuarantorCredit — the values snapshotted just before the first
// edit (so reverting an edit clears it again); the snapshot is dropped when a
// guarantor is picked/looked up.
const CREDIT_REPORT_FIELDS = [
  "creditScoreActual",
  "originalCreditScore",
  "creditReportDate",
];

const creditDiffersFromBaseline = (borrowerInfo: any) => {
  const baseline = borrowerInfo?.existingGuarantorCredit;
  if (!baseline) return false;
  return CREDIT_REPORT_FIELDS.some(
    (key) => String(borrowerInfo?.[key] ?? "") !== String(baseline[key] ?? ""),
  );
};

export const withGuarantorFlag = (borrowerInfo: any) => {
  const { guarantorId, guarantorEmail, existingGuarantorEmail } =
    borrowerInfo ?? {};
  const hasGuarantor = Boolean(guarantorId) || Boolean(guarantorEmail);
  const isNew =
    hasGuarantor &&
    (!guarantorId ||
      (existingGuarantorEmail != null &&
        normalizeEmail(guarantorEmail) !==
          normalizeEmail(existingGuarantorEmail)));
  const creditEdited = hasGuarantor && creditDiffersFromBaseline(borrowerInfo);
  return {
    ...borrowerInfo,
    updatedExistingBorrower: Boolean(isNew || creditEdited),
  };
};

export const useLoanSizingHandler = () => {
  const { dynamicConfig } = useContext(RootContext);
  const {
    detailedLoanSizingData,
    setDetailedLoanSizingData,
    setNewDebouncedValue,
    detailedLoanSizingErrors,
    setDetailedLoanSizingErrors,
    addressOptions,
    setAddressOptions,
    setZipCodeOptions,
    setBorrowerOptions,
    setGuarantorOptions,
    guarantorOptions,
    borrowerOptions,
    loanDetails,
    updateDetailedLoanSizingData,
    pendingAuditEdits,
    setPendingAuditEdits,
    pricingAdjustmentOverrides,
    setPricingAdjustmentOverrides,
    leverageAdjustedMaxOverrides,
    setLeverageAdjustedMaxOverrides,
  } = useContext(LoansContext);

  const [zipCodeInputValue, setZipCodeInputValue] = useState<string>("");
  const [addressInputValue, setAddressInputValue] = useState<string>("");
  const [borrowerNameInputValue, setBorrowerNameInputValue] =
    useState<string>("");
  const [guarantorNameInputValue, setGuarantorNameInputValue] =
    useState<string>("");

  const fetchAddress = useCallback(
    debounce(async (request: { input: string }) => {
      try {
        const { data } = await getAutocompleteAddress(
          dynamicConfig?.ViteAppSmartyStreetsApiKey,
          request.input,
        );
        setAddressOptions([...data.suggestions]);
      } catch (err) {
        console.error(err);
      }
    }, 300),
    [dynamicConfig?.ViteAppSmartyStreetsApiKey, setAddressOptions],
  );

  const fetchZipCodeSuggestions = useCallback(
    debounce(async (request: { input: string }) => {
      try {
        const { data } = await getZipCodeList(
          dynamicConfig?.ViteAppSmartyStreetsApiKey,
          request.input,
        );
        const { city_states, zipcodes } = data[0];
        let newOptions = [] as ZipCodeSuggestion[];
        if (city_states && zipcodes) {
          const zipCode = zipcodes[0].zipcode;
          const temp = city_states.map((ele: ObjectType) => ({
            ...ele,
            zipCode,
          }));
          newOptions = [...newOptions, ...temp];
        }
        setZipCodeOptions(newOptions);
      } catch (err) {
        console.error(err);
      }
    }, 200),
    [dynamicConfig?.ViteAppSmartyStreetsApiKey, setZipCodeOptions],
  );

  const updatePostalOrAddress = useCallback(
    (
      value: any,
      propertyId: number,
      section: string,
      id: string,
      isPostalCode?: boolean,
    ) => {
      let tempPropertyDetails = [
        ...(detailedLoanSizingData?.propertyDetails || []),
      ];
      setAddressOptions(value ? [value, ...addressOptions] : addressOptions);

      if (isPostalCode) {
        setZipCodeInputValue(value);
      } else {
        setAddressInputValue(value);
      }
      const objToReturn = getAddressDerivedFields(value, id, !!isPostalCode);

      if (propertyId >= 0 && propertyId < tempPropertyDetails.length) {
        tempPropertyDetails = tempPropertyDetails.map(
          (property: any, index: number) => {
            if (index === propertyId) {
              return {
                ...property,
                [section]: {
                  ...property[section],
                  ...objToReturn,
                },
              };
            }
            return property;
          },
        );
      }

      setDetailedLoanSizingData({
        ...detailedLoanSizingData,
        propertyDetails: tempPropertyDetails,
      });
    },
    [
      detailedLoanSizingData,
      addressOptions,
      setAddressOptions,
      setDetailedLoanSizingData,
    ],
  );

  const borrowerFieldMap: Record<string, (value: any) => Record<string, any>> =
    {
      borrowingEntityName: (value) => ({
        borrowingEntityName: value?.fullName,
        borrowerEmail: value?.email,
        id: value?.borrowerId,
      }),
      borrowerName: (value) => ({
        borrowerName:
          `${value?.firstName || ""} ${value?.lastName || ""}`.trim(),
        firstName: value?.firstName,
        lastName: value?.lastName,
        borrowerEmail: value?.email,
        id: value?.borrowerId,
        // check-borrower-email returns the selected (Individual) borrower's
        // FICO score and the date it was pulled — auto-fill both the credit
        // score and creditReportDate fields from it. Written directly (not
        // via SPECIAL_FIELD_UPDATES) so this select doesn't immediately wipe
        // out the very creditReportDate value it just supplied.
        ...(value?.ficoScore != null
          ? {
              creditScoreActual: String(value.ficoScore),
              originalCreditScore: String(value.ficoScore),
            }
          : {}),
        creditReportDate: value?.creditReportDate ?? null,
      }),
      guarantorName: (value) => ({
        guarantorName:
          `${value?.firstName || ""} ${value?.lastName || ""}`.trim(),
        firstName: value?.firstName,
        lastName: value?.lastName,
        guarantorEmail: value?.email ?? null,
        guarantorId: value?.borrowerId ?? null,
        existingGuarantorEmail: value?.email ?? null,
        existingGuarantorCredit: undefined,
        updatedExistingBorrower: false,
        // check-borrower-email returns the selected guarantor's FICO score
        // and the date it was pulled, same as it does for an Individual
        // borrowerName selection — auto-fill both here too.
        ...(value?.ficoScore != null
          ? {
              creditScoreActual: String(value.ficoScore),
              originalCreditScore: String(value.ficoScore),
            }
          : {}),
        creditReportDate: value?.creditReportDate ?? null,
      }),
    };

  // handleChange's `value` is frequently not what the user actually sees:
  // a dropDown's value is a raw code (e.g. citizenshipStatus's options store
  // "Yes"/"No" under a translated label like "US Citizen" — see DropDown.tsx's
  // t(ele.label)), and an autoComplete's value is the whole raw suggestion
  // object (a borrower/guarantor option — address/postalCode's own
  // multi-field case is handled separately in handleChange, via
  // getAddressDerivedFields, since selecting one of those also silently
  // changes city/state/postalCode and each needs its own audit entry).
  // Recording that raw value as this field's audit entry is what made the
  // audit trail (AuditTag.tsx) show "No" for a citizenship dropdown — this
  // derives the same display value the field itself resolves to (reusing the
  // exact same mappers applyGeneralSectionChange already uses) so the two
  // can never drift apart.
  function getAuditDisplayValue(value: any, type: string, field: Field, id: string): any {
    if (borrowerFieldMap[id]) {
      return borrowerFieldMap[id](value)?.[id];
    }
    if (type === "dropDown" && Array.isArray(field?.data)) {
      // label is an i18n key, same as what DropDown.tsx renders — AuditTag
      // translates it the same way for display.
      const matchedOption = field.data.find((option: any) => option?.value === value);
      if (matchedOption?.label) return matchedOption.label;
    }
    return value;
  }

  // Applies a change to a property-level (or unit-level) field, including
  // the propertyEconomics auto-calculated costBasis/costBasisCalculated
  // side-effects. Extracted from handleChange to keep its complexity low.
  function applyPropertyLevelSectionChange(
    value: any,
    section: string,
    id: string,
    propertyId: number,
    unitId: number,
  ) {
    let tempPropertyDetails = [
      ...(detailedLoanSizingData?.propertyDetails || []),
    ];

    if (propertyId >= 0 && propertyId < tempPropertyDetails.length) {
      tempPropertyDetails = tempPropertyDetails.map(
        (property: any, index: number) => {
          if (index === propertyId) {
            if (unitId >= 0 && unitId < property?.unitInformation?.length) {
              const currentUnitInfo = property[section]?.map(
                (unit: any, uId: number) =>
                  uId === unitId ? { ...unit, [id]: value } : unit,
              );
              return {
                ...property,
                [section]: currentUnitInfo,
              };
            }
            if (
              section === "propertyEconomics" &&
              [
                "borrowerPaidCostToDate",
                "purchasePrice",
                "closingCosts",
              ]?.includes(id)
            ) {
              return {
                ...property,
                [section]: {
                  ...property[section],
                  [id]: value,
                  ["costBasis"]: getAutoCalculatedResults(
                    "costBasis",
                    {
                      ...property,
                      [section]: { ...property[section], [id]: value },
                    },
                    {
                      isMultifamily:
                        loanDetails?.propertyType ===
                        "Multifamily/MixedUse",
                    },
                  ),
                  ["costBasisCalculated"]: id === "purchasePrice" ? +(value || 0) + +(property?.propertyEconomics?.verifiedCostsAndImprovements || 0) : property?.propertyEconomics?.costBasisCalculated
                },
              };
            }
            if (section === "propertyEconomics" && ["verifiedCostsAndImprovements"]?.includes(id)) {
              return {
                ...property,
                [section]: {
                  ...property[section],
                  [id]: value,
                  ["costBasisCalculated"]: +(property?.propertyEconomics?.propertyAcquisitionPrice || property?.propertyEconomics?.purchasePrice || 0) + +(value || 0)
                },
              };
            }
            return {
              ...property,
              [section]: {
                ...property[section],
                [id]: value,
              },
            };
          }
          return property;
        },
      );
    }

    setDetailedLoanSizingData({
      ...detailedLoanSizingData,
      propertyDetails: tempPropertyDetails,
    });
  }

  // Applies a change to a non-property-level field, including the
  // borrower/guarantor field-mapping and special-field-update side-effects.
  // Extracted from handleChange to keep its complexity low.
  function applyGeneralSectionChange(
    value: any,
    field: Field,
    section: string,
    id: string,
    propertyId: number,
  ) {
    let returnObj = {
      ...detailedLoanSizingData,
      [section]: {
        ...detailedLoanSizingData?.[section],
        [id]: value,
      },
    };

    if (id === "borrowerType") {
      // Borrower Type is a structural switch (Entity <-> Individual, each
      // with its own set of fields — see BorrowerInformation.ts's isVisible
      // gates) rather than an ordinary field edit. Nulling an enumerated
      // list of "the fields that mattered for the old type" reliably missed
      // fields as the section grew over time (TA-1234) — e.g. stale
      // borrowingEntityName/guarantor data survived a switch to Individual
      // and broke submission. Replacing the whole section can't miss
      // anything that way; also clears its validation errors, since a
      // leftover "required" error for a field that's no longer even shown
      // was part of the same submit-time breakage.
      returnObj = {
        ...detailedLoanSizingData,
        [section]: { borrowerType: value },
      };
      setBorrowerOptions([]);
      setGuarantorOptions([]);
      setBorrowerNameInputValue("");
      setGuarantorNameInputValue("");
      setDetailedLoanSizingErrors({
        ...detailedLoanSizingErrors,
        [section]: {},
      });
    } else if (borrowerFieldMap[id]) {
      returnObj = {
        ...detailedLoanSizingData,
        [section]: {
          ...detailedLoanSizingData?.[section],
          ...borrowerFieldMap[id](value), // spreads the mapped fields
        },
      };
    }
    const specialUpdate =
      SPECIAL_FIELD_UPDATES[id as keyof typeof SPECIAL_FIELD_UPDATES];
    // borrowerType already fully replaced the section above (including
    // dropping firstTimeHomeBuyer entirely) — applying its SPECIAL_FIELD_UPDATES
    // entry on top would re-add firstTimeHomeBuyer as an explicit null,
    // undoing the "replace can't miss anything" guarantee that branch exists for.
    if (specialUpdate && id !== "borrowerType") {
      returnObj = {
        ...returnObj,
        [section]: {
          ...returnObj[section],
          ...specialUpdate(value, propertyId),
        },
      };
    }
    if (
      section === PRICING_OVERRIDE_SECTION &&
      (PRICING_OVERRIDE_FIELDS as readonly string[]).includes(id)
    ) {
      // Marks this value as a genuine, hand-typed Credit-Committee override
      // (see LoanEconomics.ts's getDisplayValue) — this is the only place
      // this flag is ever set true, so buydown selection and guideline/
      // partner API writes to this same loanEconomics.interestRate/
      // finalToorakPrice key never look like a manual override.
      returnObj = {
        ...returnObj,
        [section]: {
          ...returnObj[section],
          pricingOverrideFlags: {
            ...returnObj[section]?.pricingOverrideFlags,
            [id]: true,
          },
        },
      };
    }
    // With an ENTITY borrower these credit fields belong to the guarantor:
    // editing them on a linked (existing) guarantor must flag it as updated.
    if (
      section === "borrowerInformation" &&
      CREDIT_REPORT_FIELDS.includes(id) &&
      returnObj[section]?.borrowerType?.toUpperCase?.() === "ENTITY" &&
      returnObj[section]?.guarantorId
    ) {
      const prevInfo = detailedLoanSizingData?.[section];
      const existingGuarantorCredit =
        prevInfo?.existingGuarantorCredit ??
        Object.fromEntries(
          CREDIT_REPORT_FIELDS.map((key) => [key, prevInfo?.[key] ?? null]),
        );
      const next = { ...returnObj[section], existingGuarantorCredit };
      returnObj = {
        ...returnObj,
        [section]: {
          ...next,
          updatedExistingBorrower: creditDiffersFromBaseline(next),
        },
      };
    }
    // Apply the field edit itself synchronously and unconditionally — same
    // as every other field — instead of deferring it to the guarantor
    // lookup's async resolution below, which previously meant the edit
    // never landed at all when borrowerId was empty (early return).
    if (id === "guarantorEmail" || id === "guarantorName") {
      const prevInfo = detailedLoanSizingData?.borrowerInformation;
      returnObj = {
        ...returnObj,
        [section]: withGuarantorFlag({
          ...returnObj[section],
          // Linked before the baseline email was tracked (saved loan): the
          // email held before this first edit is the existing guarantor's.
          existingGuarantorEmail:
            returnObj[section]?.existingGuarantorEmail ??
            (prevInfo?.guarantorId ? prevInfo?.guarantorEmail : null),
        }),
      };
    }
    setDetailedLoanSizingData(returnObj);
    if (id === "borrowingEntityName") {
      getGuarantorDetails(value?.borrowerId, dynamicConfig?.fileUploadUrl);
    }
  }

  const handleChange = useCallback(
    (
      value: any,
      type: string,
      field: Field,
      section: string,
      propertyId: number,
      unitId: number,
    ) => {
      const { id } = field;

      if (PROPERTY_LOCATION_FIELDS.includes(id as any)) {
        // Selecting an address/postalCode suggestion silently changes more
        // than the one field the user picked (see getAddressDerivedFields) —
        // every one of those derived fields (city, state, and postalCode/
        // address) gets its own audit entry here, not just the one the user
        // directly interacted with.
        const derivedFields = getAddressDerivedFields(value, id, id === "postalCode");
        setPendingAuditEdits?.({
          ...pendingAuditEdits,
          ...Object.fromEntries(
            Object.entries(derivedFields).map(([derivedId, derivedValue]) => [
              getAuditFieldPath(section, derivedId, propertyId, unitId),
              { value: derivedValue, comment: DEFAULT_AUDIT_COMMENT },
            ]),
          ),
        });
      } else {
        const auditPath = getAuditFieldPath(section, id, propertyId, unitId);
        setPendingAuditEdits?.({
          ...pendingAuditEdits,
          [auditPath]: {
            value: getAuditDisplayValue(value, type, field, id),
            comment: DEFAULT_AUDIT_COMMENT,
          },
        });
      }

      if (PROPERTY_LOCATION_FIELDS.includes(id as any)) {
        updatePostalOrAddress(
          value,
          propertyId,
          section,
          id,
          id === "postalCode",
        );
      } else if (
        PROPERTY_LEVEL_SECTIONS.includes(
          section as (typeof PROPERTY_LEVEL_SECTIONS)[number],
        )
      ) {
        applyPropertyLevelSectionChange(value, section, id, propertyId, unitId);
      } else {
        applyGeneralSectionChange(value, field, section, id, propertyId);
      }

      if (["dropDown", "autoComplete"].includes(type)) {
        onBlur(value, type, field, section, propertyId, unitId);
      }
      setNewDebouncedValue(value);
    },
    [
      detailedLoanSizingData,
      setDetailedLoanSizingData,
      updatePostalOrAddress,
      setNewDebouncedValue,
      pendingAuditEdits,
      setPendingAuditEdits,
      pricingAdjustmentOverrides,
      setPricingAdjustmentOverrides,
      leverageAdjustedMaxOverrides,
      setLeverageAdjustedMaxOverrides,
    ],
  );

  const onBlur = useCallback(
    (
      value: any,
      type: string,
      field: Field,
      section: string,
      propertyId: number,
      unitId: number,
    ) => {
      const errorMessage: any = field?.getFieldErrors?.(value);
      const { id } = field;

      if (
        PROPERTY_LOCATION_FIELDS.includes(
          id as (typeof PROPERTY_LOCATION_FIELDS)[number],
        )
      ) {
        updatePostalOrAddress(
          value,
          propertyId,
          section,
          id,
          id === "postalCode",
        );
      } else if (
        PROPERTY_LEVEL_SECTIONS.includes(
          section as (typeof PROPERTY_LEVEL_SECTIONS)[number],
        )
      ) {
        let tempPropertyDetails = [
          ...(detailedLoanSizingErrors?.propertyDetails || [{}]),
        ];

        if (propertyId >= 0 && propertyId < tempPropertyDetails.length) {
          tempPropertyDetails = tempPropertyDetails.map(
            (property: any, index: number) => {
              if (index === propertyId) {
                if (unitId >= 0 && unitId < property?.unitInformation?.length) {
                  const currentUnitInfo = property[section]?.map(
                    (unit: any, uId: number) =>
                      uId === unitId ? { ...unit, [id]: errorMessage } : unit,
                  );
                  return {
                    ...property,
                    [section]: currentUnitInfo,
                  };
                }
                return {
                  ...property,
                  [section]: {
                    ...property[section],
                    [id]: errorMessage,
                  },
                };
              }
              return property;
            },
          );
        }

        setDetailedLoanSizingErrors({
          ...detailedLoanSizingErrors,
          propertyDetails: tempPropertyDetails,
        });
      } else {
        setDetailedLoanSizingErrors({
          ...detailedLoanSizingErrors,
          [section]: {
            ...detailedLoanSizingErrors?.[section],
            [id]: errorMessage,
          },
        });
      }
    },
    [
      detailedLoanSizingErrors,
      updatePostalOrAddress,
      setDetailedLoanSizingErrors,
    ],
  );

  const debouncedBorrowerFetch = useMemo(
    () => debounce((borrowerObj: any) => getBorrowers(borrowerObj), 3000),
    [],
  );

  async function getBorrowers(borrowerObj: any) {
    try {
      if (!borrowerObj.username || !borrowerObj?.borrowerType) return;
      const payload = {
        username: borrowerObj.username,
        tenant: "sizer_to_los",
        borrowerType: borrowerObj?.borrowerType?.toUpperCase(),
        isTestParty: false,
      };
      const data = await getBorrowerDetailsBasedOnEmail(payload);
      if (borrowerObj?.borrowerType === "INDIVIDUAL") {
        setGuarantorOptions(data?.data ?? []);
      } else {
        setBorrowerOptions(data?.data ?? []);
      }
    } catch (err) {
      console.error(err);
    }
  }

  async function getGuarantorDetails(borrowerId: string, url: string) {
    try {
      if (!borrowerId) return;
      const { data } = await getGuarantorBasedOnBorrowerId(
        borrowerId,
        url?.split("/bff")[0] ?? "",
      );
      const guarantorName = data?.data?.[0]?.fullName || "";
      const guarantorEmail = data?.data?.[0]?.email || "";
      const ficoScore = data?.data?.[0]?.ficoScore;
      const creditReportDate = data?.data?.[0]?.creditReportDate ?? null;

      // Functional updater merging ONLY the fields this lookup actually
      // computed — never a caller-supplied snapshot of the rest of
      // detailedLoanSizingData. This call can resolve well after other
      // sections have been edited (it's fire-and-forget, no cancellation),
      // so spreading anything wider than these keys would silently revert
      // whatever the user typed in the meantime.
      setDetailedLoanSizingData((prev: any) => ({
        ...prev,
        borrowerInformation: {
          ...prev?.borrowerInformation,
          guarantorName,
          guarantorEmail,
          guarantorId: data?.data?.[0]?.borrowerId || "",
          existingGuarantorEmail: guarantorEmail,
          existingGuarantorCredit: undefined,
          updatedExistingBorrower: false,
          // The linked guarantor's FICO score and the date it was pulled ride
          // along on this same lookup — auto-fill both here too, same as
          // borrowerFieldMap/guarantorFieldMap already do for a manually
          // selected borrower/guarantor.
          ...(ficoScore != null
            ? {
                creditScoreActual: String(ficoScore),
                originalCreditScore: String(ficoScore),
              }
            : {}),
          creditReportDate,
        },
      }));

      // Selecting a borrowingEntityName auto-populates its linked guarantor's
      // name/email here — a real, visible data change the user didn't type
      // themselves, but handleChange's normal pendingAuditEdits write (which
      // only ever runs for the entity-name field itself) never sees it. Without
      // this, guarantorName/guarantorEmail's audit trail would show no history
      // at all for a change that plainly happened on screen.
      setPendingAuditEdits?.((prev: any) => ({
        ...prev,
        [getAuditFieldPath("borrowerInformation", "guarantorName")]: {
          value: guarantorName,
          comment: DEFAULT_AUDIT_COMMENT,
        },
        [getAuditFieldPath("borrowerInformation", "guarantorEmail")]: {
          value: guarantorEmail,
          comment: DEFAULT_AUDIT_COMMENT,
        },
      }));
    } catch (err) {
      console.error(err);
    }
  }
  useEffect(() => {
    if (zipCodeInputValue.length >= 4 && zipCodeInputValue.length <= 7) {
      fetchZipCodeSuggestions({ input: zipCodeInputValue });
    }
  }, [zipCodeInputValue, fetchZipCodeSuggestions]);

  useEffect(() => {
    if (addressInputValue) {
      fetchAddress({ input: addressInputValue });
    }
  }, [addressInputValue, fetchAddress]);

  useEffect(() => {
    const borrowerType =
      detailedLoanSizingData?.borrowerInformation?.borrowerType;
    if (borrowerNameInputValue) {
      debouncedBorrowerFetch({
        username: borrowerNameInputValue,
        borrowerType: borrowerType,
      });
    }
  }, [borrowerNameInputValue, debouncedBorrowerFetch]);

  useEffect(() => {
    if (guarantorNameInputValue) {
      debouncedBorrowerFetch({
        username: guarantorNameInputValue,
        borrowerType: "INDIVIDUAL",
      });
    }
  }, [guarantorNameInputValue, debouncedBorrowerFetch]);

  return {
    handleChange,
    onBlur,
    updatePostalOrAddress,
    addressOrPostalOnInputChange: (
      value: any,
      type: any,
      field: any,
      section: any,
      propertyId: any,
    ) => {
      const { id } = field;

      // Selecting a suggestion from the dropdown fires handleChange (which
      // records this in pendingAuditEdits), but freeSolo lets the user
      // instead just type an address/zip and move on without ever picking a
      // suggestion — that path only ever ran through this function, which
      // wrote straight to detailedLoanSizingData and never touched
      // pendingAuditEdits at all. The field's value visibly changed and got
      // saved with the rest of the loan, but the audit service was never
      // told about it — a save always looked complete, yet the audit trail
      // permanently showed "No audit history" for a real edit.
      const auditPath = getAuditFieldPath(section, id, propertyId);
      setPendingAuditEdits?.({
        ...pendingAuditEdits,
        [auditPath]: {
          value: getAddressFieldDisplayValue(value, id === "postalCode"),
          comment: DEFAULT_AUDIT_COMMENT,
        },
      });

      let tempPropertyDetails = [
        ...(detailedLoanSizingData?.propertyDetails || []),
      ];

      tempPropertyDetails = tempPropertyDetails.map(
        (property: any, index: number) => {
          if (index === propertyId) {
            return {
              ...property,
              [section]: {
                ...property[section],
                [id]: value,
              },
            };
          }
          return property;
        },
      );

      setDetailedLoanSizingData({
        ...detailedLoanSizingData,
        propertyDetails: tempPropertyDetails,
      });

      if (field.id === "postalCode") {
        setZipCodeInputValue(value);
      } else {
        setAddressInputValue(value);
      }
    },
    borrowerOnInputChange: (value: any, id: string) => {
      // if value is an object (selected option), ignore — handleChange already handled it
      if (typeof value === "object" && value !== null) return;

      const updated = {
        ...detailedLoanSizingData,
        borrowerInformation: {
          ...detailedLoanSizingData?.borrowerInformation,
          [id]: value,
        },
      };

      if (["borrowingEntityName", "borrowerName"].includes(id)) {
        updated.borrowerInformation = {
          ...updated.borrowerInformation,
          id:
            borrowerOptions?.find((g: any) => [value].includes(g.fullName))
              ?.borrowerId ?? null,
        };
      }
      updateDetailedLoanSizingData(updated); // ← syncs ref + state together
      setBorrowerNameInputValue(value);
    },
    guarantorOnInputChange: (value: any, id: string) => {
      // if value is an object (selected option), ignore — handleChange already handled it
      if (typeof value === "object" && value !== null) return;

      const updated = {
        ...detailedLoanSizingData,
        borrowerInformation: {
          ...detailedLoanSizingData?.borrowerInformation,
          [id]: value,
        },
      };

      if (["guarantorEmail", "guarantorName"].includes(id)) {
        const matched = guarantorOptions?.find(
          (g: any) =>
            g.email === updated.borrowerInformation.guarantorEmail &&
            g.fullName === updated.borrowerInformation.guarantorName,
        );
        updated.borrowerInformation = withGuarantorFlag({
          ...updated.borrowerInformation,
          guarantorId: matched?.borrowerId ?? null,
          existingGuarantorEmail: matched
            ? matched.email
            : updated.borrowerInformation.existingGuarantorEmail,
        });
      }
      updateDetailedLoanSizingData(updated); // ← syncs ref + state together
      setGuarantorNameInputValue(value);
    },
  };
};
